/*
 * Live personalisation for the backpacks.
 *
 * The flow is a short wizard rather than a wall of controls: our customers are
 * mostly grandparents on phones, so each step asks one question and the preview
 * stays pinned to the top of the screen while they answer it.
 *
 * This element owns its own <form>. The theme adds to cart over fetch(), and an
 * AJAX cart silently drops file uploads — but the finished mockup has to reach
 * the order for the warehouse to work from, so we post a real multipart form to
 * /cart/add and let the browser handle the navigation. `return_to` decides
 * where the customer lands: back on the product page for "add another bag", or
 * straight to checkout for "buy it now".
 */

if (!customElements.get('product-personalizer')) {
  customElements.define(
    'product-personalizer',
    class ProductPersonalizer extends HTMLElement {
      connectedCallback() {
        const data = this.querySelector('script[type="application/json"]');
        if (!data) return;

        try {
          this.config = JSON.parse(data.textContent);
        } catch (e) {
          console.error('[personalizer] bad config', e);
          return;
        }

        this.form = this.querySelector('[data-personalizer-form]');
        this.nameInput = this.querySelector('[data-personalizer-name]');
        this.counter = this.querySelector('[data-personalizer-counter]');
        this.nameField = this.querySelector('[data-personalizer-namefield]');
        this.chip = this.querySelector('[data-personalizer-chip]');
        this.chipName = this.querySelector('[data-personalizer-chip-name]');
        this.steps = this.querySelector('[data-personalizer-steps]');
        this.panes = Array.from(this.querySelectorAll('[data-personalizer-pane]'));
        this.bagList = this.querySelector('[data-personalizer-bagsw]');
        this.errorEl = this.querySelector('[data-personalizer-error]');
        this.confirmBox = this.querySelector('[data-personalizer-confirm]');
        this.chosenBox = this.querySelector('[data-personalizer-chosen]');
        this.recapGrid = this.querySelector('[data-personalizer-recap]');
        this.pickerBox = this.querySelector('[data-personalizer-picker]');
        this.changeBtn = this.querySelector('[data-personalizer-changedecor]');
        this.step3Title = this.querySelector('[data-personalizer-step3-title]');
        this.recapThumb = this.querySelector('[data-personalizer-thumb]');
        this.recapName = this.querySelector('[data-personalizer-summary-name]');
        this.recapDetail = this.querySelector('[data-personalizer-summary-detail]');
        this.recapPrice = this.querySelector('[data-personalizer-summary-price]');
        this.back3Btn = this.querySelector('[data-personalizer-back3]');
        this.doneBtn = this.querySelector('[data-personalizer-done]');
        this.productEl = this.closest('.product');
        this.mediaWrappers = Array.from(document.querySelectorAll('.product__media-wrapper'));
        this.addBtn = this.querySelector('[data-personalizer-add]');
        this.buyBtn = this.querySelector('[data-personalizer-buy]');
        this.fileInput = this.querySelector('[data-personalizer-file]');
        this.variantInput = this.querySelector('[data-personalizer-variant]');
        this.returnInput = this.querySelector('[data-personalizer-return]');
        this.addedBox = this.querySelector('[data-personalizer-added]');

        // The preview is rendered twice — sticky on mobile, in the media column
        // on desktop — and both copies have to stay in step.
        this.previews = Array.from(this.querySelectorAll('[data-personalizer-preview]'));
        document.querySelectorAll('[data-personalizer-preview]').forEach((p) => {
          if (!this.previews.includes(p)) this.previews.push(p);
        });
        // The theme's own price block sits above the fold, so it has to move with
        // the decoration count too or the two disagree. `main-price` is the
        // theme's marker for the live figure, not the struck-through compare-at.
        this.priceEls = Array.from(
          document.querySelectorAll('[data-personalizer-price], .product__info-container .price .main-price')
        );

        this.yarn = window.YarnRenderer ? new window.YarnRenderer(this.config) : null;
        this.drawToken = 0;
        this.decorSeeded = false;
        this.pickerOpen = true;
        this.pickerAuto = true;
        this.browsing = false;

        this.state = {
          name: '',
          size: '',
          bag: '',
          yarn: '',
          accents: [],
          step: 0
        };

        this.indexOptions();
        this.seedColours();
        this.dressSizeButtons();
        this.dressYarnSwatches();
        this.bindEvents();
        this.render();

        if (new URLSearchParams(location.search).get('added') === '1') {
          this.showAdded();
          /*
           * They land at the top of the page after the redirect, a long way from
           * the thing that just happened — and the offer to personalise a second
           * bag is in the banner, so missing it means missing the second sale.
           *
           * Scrolling once is not enough: the gallery above it is still loading,
           * and every image that lands pushes the banner further down than
           * wherever we just scrolled to. So we go again once the page has
           * finished settling.
           */
          /*
           * The theme sets `scroll-behavior: smooth` on <html>, and WebKit
           * ignores the `behavior` option when it does — passing 'auto' is not
           * enough, the property itself has to come off for the call. Without
           * that the jump is queued as an animation, the page is still settling
           * under it, and it silently never arrives.
           */
          const land = () => this.instantly(() => this.addedBox.scrollIntoView({ block: 'center' }));
          requestAnimationFrame(land);
          if (document.readyState === 'complete') setTimeout(land, 0);
          else window.addEventListener('load', () => setTimeout(land, 0), { once: true });
        }
      }

      /* ---------- variant plumbing ---------- */

      indexOptions() {
        const order = this.config.optionOrder || [];
        this.idx = {
          colour: order.indexOf(this.config.options.colour),
          size: order.indexOf(this.config.options.size),
          decor: order.indexOf(this.config.options.decor)
        };

        /*
         * The Decorations option carries the add-on price, so what matters is
         * how many were chosen, not which. Merchandising renames these values
         * freely ("No Decorations", "1 Decoration"), and at one point zero sat
         * last in the list — so read the count out of the label rather than
         * trusting its position.
         */
        this.decorByCount = {};
        (this.config.decorValues || []).forEach((label) => {
          const m = String(label).match(/\d+/);
          this.decorByCount[m ? parseInt(m[0], 10) : 0] = label;
        });
      }

      decorValueFor(count) {
        if (this.decorByCount[count] !== undefined) return this.decorByCount[count];
        const keys = Object.keys(this.decorByCount).map(Number).sort((a, b) => b - a);
        for (const k of keys) if (k <= count) return this.decorByCount[k];
        return this.config.decorValues[0];
      }

      currentVariant() {
        const want = [];
        want[this.idx.colour] = this.state.bag;
        want[this.idx.size] = this.state.size;
        want[this.idx.decor] = this.decorValueFor(this.state.accents.length);
        return (this.config.variants || []).find((v) =>
          want.every((val, i) => val === undefined || v.options[i] === val)
        );
      }

      /*
       * A name for a size is not a size. Most customers never scroll the gallery
       * as far as the comparison shot, so each button carries the bag it is
       * offering.
       */
      dressSizeButtons() {
        this.querySelectorAll('[data-personalizer-size]').forEach((btn) => {
          const slot = btn.querySelector('[data-personalizer-pill-art]');
          if (!slot) return;
          const base = (this.config.bases || []).find(
            (b) => b.size === btn.dataset.personalizerSize && b.image
          );
          if (!base) return;
          const img = document.createElement('img');
          img.src = base.image;
          img.alt = '';
          img.loading = 'lazy';
          slot.appendChild(img);
        });
      }

      /*
       * A flat disc of colour is not what they are choosing. Each swatch is a
       * loop from the glyph atlas, tinted to that yarn and blown up past the
       * edges of the circle, so what they see is the actual strand.
       */
      async dressYarnSwatches() {
        const buttons = Array.from(this.querySelectorAll('[data-personalizer-yarn]'));
        if (!this.yarn || !buttons.length) return;

        await this.yarn.ready();
        if (!(await this.yarn.loadGlyph('o', false))) return;

        buttons.forEach((btn) => {
          const colour = this.findYarn(btn.dataset.personalizerYarn);
          if (!colour) return;
          const art = this.yarn.tinted('o', false, colour.thread || colour.swatch);
          if (!art) return;

          const S = 128;
          const canvas = document.createElement('canvas');
          canvas.width = S;
          canvas.height = S;
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = colour.swatch;
          ctx.fillRect(0, 0, S, S);

          const scale = (S * 1.75) / Math.max(art.width, art.height);
          const w = art.width * scale;
          const h = art.height * scale;
          ctx.drawImage(art, (S - w) / 2, (S - h) / 2, w, h);

          btn.style.backgroundImage = `url(${canvas.toDataURL()})`;
        });
      }

      /*
       * Makes the selection match what the preview is already showing.
       *
       * The preview has to draw *some* bag, so with nothing chosen it falls
       * back to the first colour — and a customer who wanted that colour
       * therefore never tapped it. They walked all the way to the buy button
       * and found it greyed out with nothing on screen to say why.
       */
      seedColours() {
        if (!this.state.bag) {
          const base = this.basesForSize(this.state.size)[0];
          if (base) this.state.bag = base.color;
        }
        if (!this.state.yarn) {
          const first = (this.config.yarns || []).find((y) => !this.yarnBlocked(y));
          if (first) this.state.yarn = first.code;
        }
      }

      /* Some yarns vanish against some bags; the rule lives on the metaobject. */
      yarnBlocked(yarn) {
        return String(yarn.hideFor || '')
          .split(',')
          .map((v) => v.trim().toLowerCase())
          .filter(Boolean)
          .includes(String(this.state.bag || '').toLowerCase());
      }

      /* ---------- data lookups ---------- */

      findYarn(code) { return (this.config.yarns || []).find((y) => y.code === code); }
      findAccent(code) { return (this.config.accents || []).find((a) => a.code === code); }

      basesForSize(size) {
        const all = this.config.bases || [];
        const matched = all.filter((b) => !b.size || !size || b.size === size);
        return matched.length ? matched : all;
      }

      currentBase() {
        return this.basesForSize(this.state.size).find(
          (b) => !this.state.bag || b.color === this.state.bag
        );
      }

      maxAccents() { return this.config.maxAccents || 2; }

      /* ---------- events ---------- */

      bindEvents() {
        this.nameInput.addEventListener('input', () => {
          const caret = this.nameInput.selectionStart;
          let value = this.nameInput.value.replace(/[^A-Za-z' -]/g, '').slice(0, this.config.maxChars);
          // A child's name starts with a capital, and the first glyph is drawn
          // from the capitals atlas regardless — so make the two agree.
          value = value.charAt(0).toUpperCase() + value.slice(1);
          this.state.name = value;
          if (this.nameInput.value !== value) {
            this.nameInput.value = value;
            if (caret !== null) this.nameInput.setSelectionRange(caret, caret);
          }

          const started = this.state.name && this.state.step === 0;
          if (started) this.state.step = 1;
          // The first letter swaps the photo gallery for the preview, which
          // changes the height of everything above the field they are typing
          // in. Left alone the page lurches and the field disappears under the
          // preview mid-word.
          this.keepAnchored(this.nameInput, () => this.render());
          if (started && document.activeElement !== this.nameInput) this.scrollToStep();
        });

        // Collapsing while they are still typing swallowed the first letter, so
        // the field only folds into the chip once they have moved on.
        this.nameInput.addEventListener('blur', () => {
          this.nameCollapsed = !!this.state.name;
          this.render();
          if (this.state.name) setTimeout(() => this.scrollToStep(), 160);
        });

        this.chip.addEventListener('click', () => {
          this.nameCollapsed = false;
          this.render();
          this.nameInput.focus();
          this.nameInput.select();
        });

        this.querySelectorAll('[data-personalizer-size]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.state.size = btn.dataset.personalizerSize;
            const stillMade = this.basesForSize(this.state.size).some(
              (b) => b.color === this.state.bag
            );
            if (!stillMade) this.state.bag = '';
            this.seedColours();
            this.setStep(2);
          });
        });

        this.querySelectorAll('[data-personalizer-yarn]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.state.yarn = btn.dataset.personalizerYarn;
            this.render();
            this.maybeAdvance();
          });
        });

        this.querySelectorAll('[data-personalizer-goto]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.setStep(parseInt(btn.dataset.personalizerGoto, 10));
          });
        });

        const next = this.querySelector('[data-personalizer-next]');
        if (next) {
          next.addEventListener('click', () => {
            this.pickerOpen = true;
            this.seedDecor();
            this.setStep(3);
          });
        }

        this.querySelectorAll('[data-personalizer-accent]').forEach((btn) => {
          btn.addEventListener('click', () => {
            if (btn.dataset.blocked === 'true') return;
            const code = btn.dataset.personalizerAccent;
            const at = this.state.accents.indexOf(code);
            const added = at < 0;
            if (added) this.state.accents.push(code);
            else this.state.accents.splice(at, 1);

            /*
             * Twelve charms is a long scroll to get past, so the grid folds
             * itself away and puts the recap and the buy button next on screen
             * — but only once they have actually finished choosing.
             *
             * "Finished" means nothing further can be added: they have hit the
             * limit, or the piece they picked fills the pouch and rules the
             * rest out. Folding on any add cut them off at two, because one
             * charm is already selected when they arrive and their first tap
             * is really their second.
             *
             * Adds only, and only the first time. Taking a charm off is the
             * start of a change, not the end of one, and folding the grid under
             * them mid-change is what sent them out to the colours and back to
             * finish it.
             */
            const can = this.availability();
            const roomLeft = (this.config.accents || []).some(
              (a) => !this.state.accents.includes(a.code) && can(a)
            );
            const folded = added && !roomLeft && this.closePicker();
            this.render();
            if (folded) this.revealRecap();
          });
        });

        const none = this.querySelector('[data-personalizer-none]');
        if (none) none.addEventListener('click', () => {
          this.state.accents = [];
          const folded = this.closePicker();
          this.render();
          if (folded) this.revealRecap();
        });

        if (this.changeBtn) this.changeBtn.addEventListener('click', () => this.openPicker());
        if (this.doneBtn) {
          this.doneBtn.addEventListener('click', () => {
            this.pickerOpen = false;
            this.render();
            this.revealRecap();
          });
        }

        // Once the grid is folded away, "back" means back into it. Going out to
        // the colours and forward again was the long way round to swap a charm.
        if (this.back3Btn) {
          this.back3Btn.addEventListener('click', () => {
            if (this.pickerOpen) this.setStep(2);
            else this.openPicker();
          });
        }

        if (this.confirmBox) this.confirmBox.addEventListener('change', () => this.syncButtons());

        this.addBtn.addEventListener('click', () => { this.mode = 'add'; });
        this.buyBtn.addEventListener('click', () => { this.mode = 'buy'; });
        this.form.addEventListener('submit', (e) => this.onSubmit(e));

        this.previews.forEach((p) => {
          const back = p.querySelector('[data-personalizer-back]');
          if (back) {
            back.addEventListener('click', () => {
              this.browsing = !this.browsing;
              this.render();
              if (this.browsing) {
                const wrapper = this.mediaWrappers[0];
                if (wrapper) {
                  window.scrollTo({
                    top: Math.max(0, wrapper.getBoundingClientRect().top + window.scrollY - 8),
                    behavior: 'smooth'
                  });
                }
              } else {
                this.scrollToStep();
              }
            });
          }
        });

        // One rAF per frame at most: the handler reads layout, and running it
        // per scroll event on a phone is what made the old version stutter.
        this.onScroll = () => {
          if (this.rafPending) return;
          this.rafPending = true;
          requestAnimationFrame(() => {
            this.rafPending = false;
            this.syncSticky();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.nameInput.addEventListener('focus', () => this.syncSticky());
        this.nameInput.addEventListener('blur', () => this.syncSticky());
        if (window.visualViewport) {
          window.visualViewport.addEventListener('resize', this.onScroll);
        }
        this.onResize = () => { this.renderPreview(); this.syncSticky(); };
        window.addEventListener('resize', this.onResize);
      }

      maybeAdvance() {
        if (this.state.step !== 2 || !this.state.bag || !this.state.yarn) return;
        clearTimeout(this.advanceTimer);
        this.advanceTimer = setTimeout(() => {
          if (this.state.step !== 2) return;
          this.pickerOpen = true;
          this.seedDecor();
          this.setStep(3);
        }, 400);
      }

      /*
       * Most customers want a decoration, and asking them to opt in loses more
       * than asking them to opt out. One is preselected the first time they
       * reach this step; removing it takes a single tap.
       */
      seedDecor() {
        if (this.decorSeeded) return;
        this.decorSeeded = true;
        if (this.state.accents.length) return;
        const list = this.config.accents || [];
        const pick = list.find((a) => a.isDefault) || list.find((a) => a.zone === 'above');
        if (pick) this.state.accents = [pick.code];
      }

      /* ---------- render ---------- */

      render() {
        this.showGallery(!!this.state.name || this.state.step > 0);
        this.renderName();
        this.renderBags();
        this.renderYarns();
        this.renderAccents();
        this.renderRecap();
        this.renderSteps();
        this.renderPrice();
        this.renderPreview();
        // After renderPreview, never before: on the first keystroke the preview
        // is still display:none, so it measures zero and the sizing is skipped —
        // which left it full height for exactly the keystroke that matters.
        this.syncSticky();
        this.syncButtons();

        const back = this.previews[0] && this.previews[0].querySelector('[data-personalizer-back]');
        if (back) back.textContent = this.browsing ? this.config.text.hidePhotos : this.config.text.seePhotos;
      }

      renderName() {
        // Past step 1 the name is settled, so fold it away even if they never
        // tapped outside the field.
        const collapsed = (this.nameCollapsed || this.state.step >= 2) && !!this.state.name;
        this.nameField.hidden = collapsed;
        this.chip.hidden = !collapsed;
        if (collapsed) this.chipName.textContent = this.state.name;
        this.counter.textContent = `${this.state.name.length}/${this.config.maxChars}`;
        this.counter.classList.toggle('personalizer__counter--full', this.state.name.length >= this.config.maxChars);
      }

      renderSteps() {
        this.steps.hidden = this.state.step === 0;
        this.panes.forEach((p) => {
          p.hidden = parseInt(p.dataset.personalizerPane, 10) !== this.state.step;
        });
      }

      /* Bag colours are drawn from the base photos, so the swatch is the actual
         product shot rather than an approximation of the fabric. */
      renderBags() {
        if (!this.bagList) return;
        const bases = this.basesForSize(this.state.size);
        const key = bases.map((b) => b.color).join('|');
        if (key !== this.bagKey) {
          this.bagKey = key;
          this.bagList.innerHTML = '';
          bases.forEach((b) => {
            const li = document.createElement('li');
            li.className = 'personalizer__swwrap';
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'personalizer__sw personalizer__sw--bag';
            btn.setAttribute('aria-label', b.color);
            btn.dataset.bag = b.color;
            if (b.image) {
              const img = document.createElement('img');
              img.src = b.image;
              img.alt = '';
              img.loading = 'lazy';
              btn.appendChild(img);
            }
            btn.addEventListener('click', () => {
              this.state.bag = b.color;
              this.render();
              this.maybeAdvance();
            });
            const label = document.createElement('span');
            label.textContent = b.color;
            li.appendChild(btn);
            li.appendChild(label);
            this.bagList.appendChild(li);
          });
        }
        this.bagList.querySelectorAll('[data-bag]').forEach((btn) => {
          btn.setAttribute('aria-pressed', String(btn.dataset.bag === this.state.bag));
        });
      }

      /*
       * Some yarn colours vanish against some bags — blush on the pink bag, for
       * instance. Those pairs are removed rather than offered and regretted.
       */
      renderYarns() {
        let firstVisible = null;
        let selectedHidden = false;

        this.querySelectorAll('[data-personalizer-yarn]').forEach((btn) => {
          const blocked = this.yarnBlocked({ hideFor: btn.dataset.hideFor });

          const wrap = btn.closest('.personalizer__swwrap');
          if (wrap) wrap.hidden = blocked;
          btn.disabled = blocked;
          const code = btn.dataset.personalizerYarn;
          if (!blocked && !firstVisible) firstVisible = code;
          if (blocked && code === this.state.yarn) selectedHidden = true;
          btn.setAttribute('aria-pressed', String(code === this.state.yarn));
        });

        // Leaving them with nothing selected is the same trap: the name keeps
        // drawing in a fallback colour that no longer matches any choice.
        if (selectedHidden) {
          this.state.yarn = firstVisible || '';
          this.querySelectorAll('[data-personalizer-yarn]').forEach((b) =>
            b.setAttribute(
              'aria-pressed',
              String(b.dataset.personalizerYarn === this.state.yarn)
            )
          );
        }
      }

      /*
       * Where a decoration may sit is a property of the decoration, not a free
       * choice — the real charms differ wildly in size:
       *   above        - only the small single flowers clear the name
       *   pouch_side   - the big appliques, left or right of the pouch
       *   pouch_center - the hand-knitted ones, which fill the pouch
       * A centre piece therefore rules out both side slots, and vice versa.
       */
      assignZones() {
        const slots = { above: null, pouch_left: null, pouch_right: null, pouch_center: null };
        this.state.accents
          .map((c) => this.findAccent(c))
          .filter(Boolean)
          .forEach((a) => {
            if (a.zone === 'above' && !slots.above) { slots.above = a; return; }
            if (a.zone === 'pouch_center' && !slots.pouch_center) { slots.pouch_center = a; return; }
            if (a.zone === 'pouch_side') {
              if (!slots.pouch_left) slots.pouch_left = a;
              else if (!slots.pouch_right) slots.pouch_right = a;
            }
          });
        return slots;
      }

      availability() {
        const slots = this.assignZones();
        const atMax = this.state.accents.length >= this.maxAccents();
        const hasCentre = !!slots.pouch_center;
        const hasSide = !!(slots.pouch_left || slots.pouch_right);
        return (accent) => {
          if (this.state.accents.includes(accent.code)) return true;
          if (atMax) return false;
          if (accent.zone === 'above') return !slots.above;
          if (accent.zone === 'pouch_center') return !hasCentre && !hasSide;
          if (accent.zone === 'pouch_side') return !hasCentre && !slots.pouch_right;
          return false;
        };
      }

      renderAccents() {
        const can = this.availability();
        this.querySelectorAll('[data-personalizer-accent]').forEach((btn) => {
          const accent = this.findAccent(btn.dataset.personalizerAccent);
          if (!accent) return;
          const on = this.state.accents.includes(accent.code);
          btn.setAttribute('aria-pressed', String(on));
          btn.dataset.blocked = String(!can(accent));
        });
        const none = this.querySelector('[data-personalizer-none]');
        if (none) none.setAttribute('aria-pressed', String(this.state.accents.length === 0));
      }

      /*
       * The summary is the design itself — the picture of the bag they just
       * made, with the name on it — rather than a list of fields describing it.
       * It is the last thing they see before the buy button, and it is the same
       * card they get back after adding, so the two read as one thing.
       */
      renderRecap() {
        if (this.pickerBox) this.pickerBox.hidden = !this.pickerOpen;
        if (this.changeBtn) this.changeBtn.hidden = this.pickerOpen;
        // The question has been answered; leaving it up only crowds the answer.
        if (this.step3Title) this.step3Title.hidden = !this.pickerOpen;
        if (this.doneBtn) this.doneBtn.hidden = !this.pickerOpen;
        if (this.back3Btn) {
          this.back3Btn.textContent = this.pickerOpen
            ? this.config.text.backColours
            : this.config.text.backDecor;
        }

        if (this.recapName) this.recapName.textContent = this.state.name || '—';
        if (this.recapDetail) this.recapDetail.textContent = this.describeState();
        if (this.recapPrice) {
          const variant = this.currentVariant();
          this.recapPrice.textContent = variant ? variant.price : '';
        }
        if (!this.pickerOpen) this.scheduleThumb();
      }

      /* "Pink · School Age (Large) · Single White Flower + Strawberry" */
      describeState() {
        const parts = [];
        if (this.state.bag) parts.push(this.state.bag);
        if (this.state.size) parts.push(this.state.size);
        const charms = this.state.accents
          .map((c) => this.findAccent(c))
          .filter(Boolean)
          .map((a) => a.label);
        parts.push(charms.length ? charms.join(' + ') : this.config.text.noneChosen);
        return parts.join(' \u00b7 ');
      }

      /*
       * Redraws the little picture of the design. Debounced, because every tap
       * on a charm asks for a new one and the draw is a full composite.
       */
      scheduleThumb() {
        if (!this.recapThumb) return;
        clearTimeout(this.thumbTimer);
        this.thumbTimer = setTimeout(async () => {
          const file = await this.captureMockup(360).catch(() => null);
          if (!file) return;
          if (this.thumbUrl) URL.revokeObjectURL(this.thumbUrl);
          this.thumbUrl = URL.createObjectURL(file);
          this.recapThumb.src = this.thumbUrl;
          this.recapThumb.hidden = false;
        }, 220);
      }

      renderPrice() {
        const v = this.currentVariant();
        if (v && this.variantInput) this.variantInput.value = v.id;
        this.priceEls.forEach((el) => { if (v) el.textContent = v.price; });
      }

      renderPreview() {
        const active = !!this.state.name || this.state.step > 0;
        const base = this.currentBase();
        const anchors = (base && base.anchors) || {};
        const yarn = this.findYarn(this.state.yarn);
        const slots = this.assignZones();
        const nudges = this.config.nudge || {};

        this.previews.forEach((preview) => {
          preview.classList.toggle('personalizer-preview--active', active);
          const baseImg = preview.querySelector('[data-personalizer-base]');
          const placeholder = preview.querySelector('[data-personalizer-placeholder]');
          const nameLayer = preview.querySelector('[data-personalizer-name-canvas]');

          if (baseImg) {
            if (base && base.image) {
              if (baseImg.getAttribute('src') !== base.image) baseImg.src = base.image;
              baseImg.alt = base.title || '';
              baseImg.hidden = false;
            } else {
              baseImg.removeAttribute('src');
              baseImg.hidden = true;
            }
          }
          if (placeholder) placeholder.hidden = Boolean(base && base.image);

          if (nameLayer) {
            const anchor = anchors.name || { x: 50, y: 60, w: 38, rotate: 0 };
            nameLayer.style.left = `${anchor.x}%`;
            nameLayer.style.top = `${anchor.y}%`;
            nameLayer.style.transform = `translate(-50%, -50%) rotate(${anchor.rotate || 0}deg)`;
            this.drawName(preview, nameLayer, anchor, yarn);
          }

          ['above', 'pouch_left', 'pouch_right', 'pouch_center'].forEach((zone) => {
            const layer = preview.querySelector(`[data-personalizer-zone="${zone}"]`);
            if (!layer) return;
            const accent = slots[zone];
            const anchor = anchors[zone];
            if (!accent || !accent.art || !anchor) {
              layer.hidden = true;
              layer.removeAttribute('src');
              return;
            }
            // Theme-editor nudges shift and scale the stored anchor, so the fit
            // can be tuned live without editing the metafield JSON.
            const n = nudges[zone] || { x: 0, y: 0, scale: 100 };
            layer.hidden = false;
            if (layer.getAttribute('src') !== accent.art) layer.src = accent.art;
            layer.alt = accent.label;
            layer.style.left = `${anchor.x + (n.x || 0)}%`;
            layer.style.top = `${anchor.y + (n.y || 0)}%`;
            layer.style.width = `${anchor.w * ((n.scale || 100) / 100)}%`;
          });
        });
      }

      /*
       * The gauge is a fraction of the bag, not of the word, so a short name is
       * simply shorter rather than fatter; the anchor width caps long ones.
       */
      drawName(preview, canvas, anchor, yarn) {
        const inner = preview.querySelector('[data-personalizer-inner]');
        if (!canvas || !inner || !this.yarn) return;
        if (!this.state.name) { canvas.hidden = true; return; }

        const width = inner.clientWidth;
        if (!width) return;

        const maxW = anchor.w || 38;
        const emPct = anchor.em || maxW * 0.456;
        const colour = (yarn && (yarn.thread || yarn.swatch)) || '#F2EAD8';

        const token = ++this.drawToken;
        this.yarn
          .draw(canvas, this.state.name, colour, {
            emPx: (width * emPct) / 100,
            maxWidthPx: (width * maxW) / 100,
            shadowColor: 'rgba(70,45,40,0.28)',
            shadowBlur: 7,
            shadowX: 2,
            shadowY: 5
          })
          .then((size) => {
            if (!size || token !== this.drawToken) return;
            canvas.style.width = `${size.cssWidth}px`;
            canvas.style.height = `${size.cssHeight}px`;
            canvas.hidden = false;
          })
          .catch((e) => console.error('[personalizer] yarn draw failed', e));
      }

      showGallery(personalizing) {
        this.mediaWrappers.forEach((w) => {
          w.classList.toggle('product__media-wrapper--personalizing', personalizing);
          w.classList.toggle('product__media-wrapper--browsing', personalizing && this.browsing);
        });
        if (this.productEl) {
          this.productEl.classList.toggle('product--personalizing', personalizing);
          this.productEl.classList.toggle('product--browsing', personalizing && this.browsing);
        }
      }

      /*
       * Puts the question they are being asked directly under the pinned
       * preview. Without this the preview arrives over the top of step one and
       * they have to go looking for it.
       *
       * The preview shrinks as the page scrolls, which pulls everything below
       * it upwards — so aiming at the size it will settle at overshoots by the
       * difference. Measuring the wrapper as it is right now cancels that out:
       * whatever it collapses by, the content it was holding down comes up by
       * exactly the same amount, and the question lands just under it.
       */
      scrollToStep() {
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        const pane = this.panes.find((p) => !p.hidden);
        if (!pane) return;

        const wrapper = this.mediaWrappers.find((w) =>
          w.classList.contains('product__media-wrapper--personalizing')
        );
        const height = wrapper && !this.browsing ? wrapper.getBoundingClientRect().height : 0;

        // Leave them where they are if the question is already fully on screen —
        // scrolling for its own sake reads as the page misbehaving.
        const rect = pane.getBoundingClientRect();
        if (rect.top >= height && rect.bottom <= window.innerHeight) return;

        const top = rect.top + window.scrollY - height - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }

      /*
       * Runs `change` while holding `el` still on screen. Anything that alters
       * the height of the page above `el` would otherwise slide it out from
       * under the customer — most visibly while they are mid-word.
       */
      keepAnchored(el, change) {
        const before = el.getBoundingClientRect().top;
        change();
        const delta = el.getBoundingClientRect().top - before;
        if (Math.abs(delta) < 1) return;
        this.instantly(() => window.scrollBy(0, delta));
      }

      /* WebKit ignores the `behavior` option while CSS says smooth, so the
         property itself has to come off for the call. */
      instantly(move) {
        const root = document.documentElement;
        const previous = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        move();
        root.style.scrollBehavior = previous;
      }

      closePicker() {
        if (!this.pickerAuto) return false;
        this.pickerAuto = false;
        this.pickerOpen = false;
        return true;
      }

      /*
       * The grid folding away takes a screenful of page with it, so whatever
       * they were looking at drops out from under them. Put the answer they
       * just gave in front of them instead.
       */
      revealRecap() {
        if (!this.chosenBox) return;
        this.instantly(() => this.chosenBox.scrollIntoView({ block: 'start' }));
      }

      openPicker() {
        this.pickerAuto = false;
        this.pickerOpen = true;
        this.render();
      }

      setStep(n) {
        this.state.step = n;
        this.render();
        this.scrollToStep();
      }

      /*
       * Closes the preview window as the page scrolls, rather than snapping it
       * between two sizes.
       *
       * The measurement is taken from the top of the product grid, never from
       * the preview itself: the preview is sticky, so its own top is pinned at
       * zero once it catches, and shrinking it moves anything measured below it.
       * Reading its own box made the size the input to itself, which is why it
       * flickered between tall and short on the slightest scroll.
       */
      syncSticky() {
        const on =
          this.config.preview &&
          this.config.preview.sticky &&
          window.matchMedia('(max-width: 749px)').matches &&
          !this.browsing;

        this.previews.forEach((preview) => {
          const stage = preview.querySelector('[data-personalizer-stage]');
          const inner = preview.querySelector('[data-personalizer-inner]');
          if (!stage || !inner) return;

          if (!on) {
            stage.style.height = '';
            inner.style.top = '';
            this.style.setProperty('--sticky-h', '0px');
            return;
          }

          const full = stage.clientWidth;
          if (!full) return;
          const min = Math.min(this.config.preview.shrinkTo || 150, full);
          // Spread the collapse over more scroll than it takes back, so the page
          // underneath rises gently instead of snapping up to meet it.
          const range = (full - min) * 1.5 || 1;
          const anchor = this.productEl ? this.productEl.getBoundingClientRect().top : 0;
          /*
           * With the keyboard up the visible viewport is barely taller than the
           * preview at full size, so it covered the field being typed into and
           * there was nowhere to scroll to fix it. While they are typing the
           * preview goes straight to its smallest.
           */
          const typing = document.activeElement === this.nameInput;
          const t = typing ? 1 : Math.min(1, Math.max(0, -anchor / range));
          const eased = t * t * (3 - 2 * t);

          const height = full - (full - min) * eased;
          stage.style.height = `${height}px`;
          // Focusing an input makes the browser scroll it into view, and it has
          // no idea the top of the screen is already occupied. Reading the
          // height back also flushes the change we just made.
          this.style.setProperty('--sticky-h', `${Math.round(preview.offsetHeight)}px`);
          // Slide the square up as the window closes, so the crop settles on the
          // front of the bag rather than lopping off the bottom.
          inner.style.top = `${(height - full) * ((this.config.preview.cropBias || 0) / 100)}px`;
        });
      }

      syncButtons() {
        const v = this.currentVariant();
        const ready =
          !!this.state.name &&
          !!this.state.size &&
          !!this.state.bag &&
          !!this.state.yarn &&
          (!this.confirmBox || this.confirmBox.checked) &&
          !!v &&
          v.available;
        this.addBtn.disabled = !ready;
        this.buyBtn.disabled = !ready;
        this.buyBtn.textContent =
          this.config.cartCount > 0 ? this.config.text.buyAll : this.config.text.buy;
      }

      /* ---------- submit ---------- */

      setProp(name, value) {
        let el = this.form.querySelector(`input[name="properties[${CSS.escape(name)}]"]`);
        if (!value) { if (el) el.remove(); return; }
        if (!el) {
          el = document.createElement('input');
          el.type = 'hidden';
          el.name = `properties[${name}]`;
          this.form.appendChild(el);
        }
        el.value = value;
      }

      writeProperties() {
        const yarn = this.findYarn(this.state.yarn);
        const slots = this.assignZones();
        const PLACE = {
          above: 'above the name',
          pouch_left: 'front pocket, left',
          pouch_right: 'front pocket, right',
          pouch_center: 'front pocket, centre'
        };
        /*
         * Two audiences, one line item. The customer sees plain English; the
         * supplier codes go on underscore-prefixed properties, which Shopify
         * keeps out of the cart, the checkout and the confirmation email but
         * shows in full on the order in admin.
         */
        this.setProp('Name', this.state.name);
        this.setProp('Yarn colour', yarn ? yarn.label : '');
        this.setProp('_Yarn code', yarn ? yarn.code : '');

        let n = 0;
        Object.keys(PLACE).forEach((zone) => {
          const a = slots[zone];
          if (!a) return;
          n += 1;
          this.setProp(`Decoration ${n}`, `${a.label} — ${PLACE[zone]}`);
          this.setProp(`_Decoration ${n} code`, a.supplierCode || a.code);
        });
        for (let i = n + 1; i <= 4; i++) {
          this.setProp(`Decoration ${i}`, '');
          this.setProp(`_Decoration ${i} code`, '');
        }
        if (!n) this.setProp('Decorations', 'None');
      }

      onSubmit(event) {
        event.preventDefault();
        if (this.submitting) return;

        const v = this.currentVariant();
        if (!this.state.name) return this.fail(this.config.text.needName);
        if (!v || !v.available) return this.fail(this.config.text.needSize);

        this.submitting = true;
        this.fail('');
        const btn = this.mode === 'buy' ? this.buyBtn : this.addBtn;
        btn.classList.add('is-busy');

        this.variantInput.value = v.id;
        this.returnInput.value =
          this.mode === 'buy' ? '/checkout' : `${this.config.productUrl}?added=1#PersonalizerAdded`;
        this.writeProperties();

        this.captureMockup()
          .catch((e) => { console.warn('[personalizer] mockup capture failed', e); return null; })
          .then((file) => {
            if (file && this.fileInput) {
              try {
                const dt = new DataTransfer();
                dt.items.add(file);
                this.fileInput.files = dt.files;
              } catch (e) {
                console.warn('[personalizer] could not attach mockup', e);
              }
            }
            HTMLFormElement.prototype.submit.call(this.form);
          });
      }

      fail(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message || '';
        this.errorEl.classList.toggle('personalizer__error--visible', !!message);
        if (message) this.submitting = false;
      }

      /*
       * Redraws the design at print size so the warehouse gets something they
       * can work from, and the customer gets it back in their confirmation
       * email. Every source image is same-origin or CORS-enabled, so the canvas
       * stays untainted and toBlob works.
       */
      async captureMockup(size) {
        const base = this.currentBase();
        if (!base || !base.image) return null;

        const S = size || 1200;
        const canvas = document.createElement('canvas');
        canvas.width = S;
        canvas.height = S;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, S, S);

        const load = (src) =>
          new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => resolve(null);
            img.src = src;
          });

        const plate = await load(base.image);
        if (plate) {
          // `object-fit: contain`, worked out by hand so the capture matches.
          const k = Math.min(S / plate.naturalWidth, S / plate.naturalHeight);
          const w = plate.naturalWidth * k;
          const h = plate.naturalHeight * k;
          ctx.drawImage(plate, (S - w) / 2, (S - h) / 2, w, h);
        }

        const anchors = base.anchors || {};
        const slots = this.assignZones();
        const nudges = this.config.nudge || {};

        const nameAnchor = anchors.name || { x: 50, y: 60, w: 38, rotate: 0 };
        if (this.state.name && this.yarn) {
          const yarn = this.findYarn(this.state.yarn);
          const tmp = document.createElement('canvas');
          const maxW = nameAnchor.w || 38;
          const size = await this.yarn.draw(
            tmp,
            this.state.name,
            (yarn && (yarn.thread || yarn.swatch)) || '#F2EAD8',
            {
              emPx: (S * (nameAnchor.em || maxW * 0.456)) / 100,
              maxWidthPx: (S * maxW) / 100,
              shadowColor: 'rgba(70,45,40,0.28)',
              shadowBlur: 7,
              shadowX: 2,
              shadowY: 5
            }
          );
          if (size) {
            ctx.save();
            ctx.translate((S * nameAnchor.x) / 100, (S * nameAnchor.y) / 100);
            ctx.rotate(((nameAnchor.rotate || 0) * Math.PI) / 180);
            ctx.drawImage(tmp, -size.cssWidth / 2, -size.cssHeight / 2, size.cssWidth, size.cssHeight);
            ctx.restore();
          }
        }

        for (const zone of ['above', 'pouch_left', 'pouch_right', 'pouch_center']) {
          const accent = slots[zone];
          const anchor = anchors[zone];
          if (!accent || !accent.art || !anchor) continue;
          const art = await load(accent.art);
          if (!art) continue;
          const n = nudges[zone] || { x: 0, y: 0, scale: 100 };
          const w = (S * anchor.w * ((n.scale || 100) / 100)) / 100;
          const h = w * (art.naturalHeight / art.naturalWidth);
          const cx = (S * (anchor.x + (n.x || 0))) / 100;
          const cy = (S * (anchor.y + (n.y || 0))) / 100;
          ctx.save();
          ctx.shadowColor = 'rgba(70,45,40,0.32)';
          ctx.shadowBlur = 0.004 * S;
          ctx.shadowOffsetX = 0.0015 * S;
          ctx.shadowOffsetY = 0.0035 * S;
          ctx.drawImage(art, cx - w / 2, cy - h / 2, w, h);
          ctx.restore();
        }

        const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.86));
        if (!blob) return null;
        const slug = (this.state.name || 'design').replace(/[^A-Za-z0-9]+/g, '-').toLowerCase();
        return new File([blob], `mockup-${slug}.jpg`, { type: 'image/jpeg' });
      }

      /* ---------- after adding ---------- */

      /*
       * What they get after adding: every bag they have designed so far, each
       * with the picture of it, so a second one can be started from a page that
       * proves the first survived. A cart line that shows the stock photo after
       * ten minutes of designing reads as the tool having lost the work.
       */
      async showAdded() {
        if (!this.addedBox) return;
        this.addedBox.hidden = false;
        this.addedBox.innerHTML = '';

        const message = document.createElement('p');
        message.className = 'personalizer__added-msg';
        message.textContent = this.config.text.addedBanner;
        this.addedBox.appendChild(message);

        let cart = null;
        try {
          cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
        } catch (e) {
          console.warn('[personalizer] could not read the cart', e);
        }

        const designs = (cart?.items || []).filter((i) => i.properties && i.properties.Name);
        if (designs.length) {
          const list = document.createElement('ul');
          list.className = 'personalizer__bags';
          list.setAttribute('role', 'list');

          designs.forEach((item) => {
            const row = document.createElement('li');
            row.className = 'personalizer__bag';

            const art = item.properties['Your design'] || item.image;
            if (art) {
              const img = document.createElement('img');
              img.src = art;
              img.alt = '';
              img.loading = 'lazy';
              row.appendChild(img);
            }

            const text = document.createElement('div');
            const name = document.createElement('strong');
            name.textContent = item.properties.Name;
            text.appendChild(name);

            const detail = document.createElement('span');
            detail.textContent = this.describe(item);
            text.appendChild(detail);

            row.appendChild(text);

            const price = document.createElement('span');
            price.className = 'personalizer__bag-price';
            price.textContent = this.money(item.final_line_price);
            row.appendChild(price);

            list.appendChild(row);
          });
          this.addedBox.appendChild(list);
        }

        const actions = document.createElement('div');
        actions.className = 'personalizer__added-actions';

        const again = document.createElement('button');
        again.type = 'button';
        again.className = 'personalizer__btn personalizer__btn--add';
        again.textContent = this.config.text.addAnother;
        again.addEventListener('click', () => {
          this.addedBox.hidden = true;
          this.resetDesign();
          this.nameInput.focus();
        });

        const checkout = document.createElement('a');
        checkout.className = 'personalizer__btn personalizer__btn--buy';
        checkout.href = '/checkout';
        checkout.textContent =
          designs.length > 1
            ? `${this.config.text.checkout} (${designs.length})`
            : this.config.text.checkout;

        actions.appendChild(again);
        actions.appendChild(checkout);
        this.addedBox.appendChild(actions);
      }

      /* "Pink, School Age (Large), Single White Flower + Bunny" */
      describe(item) {
        const parts = [];
        (item.options_with_values || []).forEach((o) => {
          if (o.name !== this.config.options.decor) parts.push(o.value);
        });
        const charms = [1, 2, 3, 4]
          .map((n) => item.properties[`Decoration ${n}`])
          .filter(Boolean)
          .map((v) => String(v).split('\u2014')[0].trim());
        if (charms.length) parts.push(charms.join(' + '));
        return parts.join(' \u00b7 ');
      }

      money(cents) {
        const price = (cents / 100).toFixed(2);
        const sample = this.config.variants[0] && this.config.variants[0].price;
        // Borrow the shop's own formatting rather than guessing the symbol.
        return sample ? sample.replace(/[\d.,]+/, price) : price;
      }

      resetDesign() {
        this.state = { name: '', size: '', bag: '', yarn: '', accents: [], step: 0 };
        this.nameCollapsed = false;
        this.pickerOpen = true;
        this.pickerAuto = true;
        this.browsing = false;
        this.decorSeeded = false;
        this.nameInput.value = '';
        if (this.confirmBox) this.confirmBox.checked = false;
        this.seedColours();
        this.render();
      }
    }
  );
}
